export type Cefr = 'A1' | 'A2' | 'B1' | 'B2' | 'C1';

export type ExerciseType =
  | 'multiple_choice'
  | 'fill_blank'
  | 'reorder'
  | 'translate'
  | 'listen'
  | 'listen_choice'
  | 'image_select'
  | 'speak'
  | 'match_pairs';

export type VoicePreset = 'female' | 'male';

// Popular ElevenLabs voices — pinned here so the curriculum can request
// alternating gender without leaking voice IDs.
export const VOICE_IDS: Record<VoicePreset, string> = {
  female: 'EXAVITQu4vr4xnSDxMaL', // Sarah
  male: 'nPczCjzI2devNBz1zQrb',   // Brian
};

export type ExerciseBase = {
  id: string;
  newWord?: string;
  character?: CharacterKey;
  bubble?: string;
  keyword?: string;
  /** Alternate voice per exercise — default female, but curriculum can set 'male'. */
  voice?: VoicePreset;
};

export type IllustrationKey =
  | 'wand' | 'shield' | 'ring' | 'mask' | 'film' | 'lightning' | 'katana'
  | 'crown' | 'spaceship' | 'coffee' | 'home' | 'pizza' | 'plane' | 'bag'
  | 'brain' | 'megaphone' | 'book' | 'chat' | 'compass' | 'gamepad';

export type MultipleChoice = ExerciseBase & {
  type: 'multiple_choice';
  prompt: string;
  choices: string[];
  answerIndex: number;
  explanation?: string;
};

export type FillBlank = ExerciseBase & {
  type: 'fill_blank';
  prompt: string;
  answer: string;
  accepts?: string[];
};

export type Translate = ExerciseBase & {
  type: 'translate';
  prompt: string;
  from: 'en' | 'pt';
  to: 'en' | 'pt';
  answer: string;
  accepts?: string[];
};

export type Reorder = ExerciseBase & {
  type: 'reorder';
  prompt: string;
  tokens: string[];
  answer: string[];
};

export type Listen = ExerciseBase & {
  type: 'listen';
  prompt: string;
  audioText: string; // text spoken via ElevenLabs TTS (with speechSynthesis fallback)
  answer: string;
};

export type ListenChoice = ExerciseBase & {
  type: 'listen_choice';
  prompt: string;
  audioText: string;
  choices: string[];
  answerIndex: number;
};

export type ImageSelect = ExerciseBase & {
  type: 'image_select';
  /** Spoken question, e.g. "Which one is a wand?" */
  prompt: string;
  promptAudio: string;
  options: Array<{
    illustration: IllustrationKey;
    word: string;
    /** Audio spoken when the user taps this option (defaults to `word`). */
    audio?: string;
  }>;
  answerIndex: number;
};

export type Speak = ExerciseBase & {
  type: 'speak';
  prompt: string;
  target: string;
};

export type MatchPairs = ExerciseBase & {
  type: 'match_pairs';
  prompt: string;
  pairs: Array<{ left: string; right: string }>;
};

export type Exercise =
  | MultipleChoice
  | FillBlank
  | Translate
  | Reorder
  | Listen
  | ListenChoice
  | ImageSelect
  | Speak
  | MatchPairs;

export type Lesson = {
  id: string;
  title: string;
  popCulture: string; // "Harry Potter", "Breaking Bad"...
  xpReward: number;
  exercises: Exercise[];
};

export type Unit = {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  theme: string;
  cefr: Cefr;
  color: UnitColor;
  lessons: Lesson[];
};

export type Section = {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  cefr: Cefr;
  units: Unit[];
};

export type UnitColor =
  | 'owl'
  | 'beetle'
  | 'peacock'
  | 'macaw'
  | 'fox'
  | 'cardinal'
  | 'starfish'
  | 'butterfly'
  | 'kiwi';

export type CharacterKey =
  | 'duo'
  | 'bea'
  | 'eddy'
  | 'lily'
  | 'vikram'
  | 'zari'
  | 'oscar'
  | 'falstaff'
  | 'lucy';

export const CHARACTER_META: Record<CharacterKey, { name: string; color: string; initials: string }> = {
  duo: { name: 'Duo', color: 'rgb(114, 214, 39)', initials: 'D' },
  bea: { name: 'Bea', color: 'rgb(24, 153, 214)', initials: 'B' },
  eddy: { name: 'Eddy', color: 'rgb(234, 43, 43)', initials: 'E' },
  lily: { name: 'Lily', color: 'rgb(165, 104, 204)', initials: 'L' },
  vikram: { name: 'Vikram', color: 'rgb(163, 42, 113)', initials: 'V' },
  zari: { name: 'Zari', color: 'rgb(204, 107, 166)', initials: 'Z' },
  oscar: { name: 'Oscar', color: 'rgb(0, 164, 125)', initials: 'O' },
  falstaff: { name: 'Falstaff', color: 'rgb(150, 90, 58)', initials: 'F' },
  lucy: { name: 'Lucy', color: 'rgb(255, 168, 44)', initials: 'L' },
};

export const LEAGUE_TIERS = [
  { key: 'rookie', label: 'Rookie', color: 'rgb(0, 175, 133)' },
  { key: 'explorer', label: 'Explorer', color: 'rgb(255, 100, 191)' },
  { key: 'traveler', label: 'Traveler', color: 'rgb(255, 145, 83)' },
  { key: 'trailblazer', label: 'Trailblazer', color: 'rgb(154, 143, 232)' },
  { key: 'adventurer', label: 'Adventurer', color: 'rgb(96, 12, 199)' },
  { key: 'discoverer', label: 'Discoverer', color: 'rgb(111, 44, 57)' },
  { key: 'daredevil', label: 'Daredevil', color: 'rgb(46, 83, 138)' },
  { key: 'navigator', label: 'Navigator', color: 'rgb(9, 47, 119)' },
  { key: 'champion', label: 'Champion', color: 'rgb(255, 110, 53)' },
] as const;
